import * as React from "react";
import { useCallback, useMemo } from "react";

import { AlertTriangle, Briefcase, GalleryVerticalEnd, Loader2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { CreateOrgDialog, CreateOrgDialogTrigger } from "./create-org-dialog";
import { useAction } from "@/hooks/useAction";
import { switchOrgAction } from "./actions";
import { toast } from "sonner";
import { SidebarMenuButton } from "../ui/sidebar";
import { CaretSortIcon } from "@radix-ui/react-icons";
import { useOrgs } from "@/context/OrgsProvider";
import { useOrgsQuery } from "@/hooks/useOrgsQuery";
import { Button } from "../ui/button";
import { deleteAccount } from "@/data/groups";
import { handleLogout } from "@/lib/auth";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";


export function OrgSwitcher() {
  const { activeOrg } = useOrgs();
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } = useOrgsQuery();
  
  console.log("OrgSwitcher render - activeOrg:", activeOrg?.name);
  
  const [createOrgDialogOpen, setCreateOrgDialogOpen] = React.useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = React.useState(false);
  const [deleteAccountDialogOpen, setDeleteAccountDialogOpen] = React.useState(false);
  
  // Flatten all pages from the infinite query into a single array
  const orgs = useMemo(() => {
    if (!data?.pages) {
      return [];
    }
    return data.pages.flatMap((page) => {
      // Handle both success and error responses
      if (page.status === "success") {
        return page.results ?? [];
      }
      return [];
    });
  }, [data]);

  const ownOrgs = useMemo(() => orgs.filter((org) => !org.advised), [orgs]);
  const advisedOrgs = useMemo(() => orgs.filter((org) => org.advised), [orgs]);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;
    
    // Load more when scrolled within 50px of the bottom
    const scrollBottom = scrollHeight - scrollTop - clientHeight;
    if (scrollBottom < 50 && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  
  const { execute: switchOrg, isPending } = useAction(switchOrgAction, {
    onSuccess: () => {
      console.log("OrgSwitcher - switchOrg onSuccess called");
      toast.success("Organization switched");
      // Force a full page refresh
      window.location.reload();
    },
    onError: ({ error }) => {
      console.log("OrgSwitcher - switchOrg onError called:", error);
      toast.error(
        error?.serverError?.message ?? "Failed to switch organization",
      );
    },
  });

  const handleSwitchOrg = async (orgId: string) => {
    console.log("OrgSwitcher - handleSwitchOrg called with orgId:", orgId);
    switchOrg({ orgId });
  };

  const handleCreateOrgClick = () => {
    console.log("OrgSwitcher - handleCreateOrgClick called, setting dialog open to true");
    setCreateOrgDialogOpen(true);
  };

  const handleDeleteAccountClick = () => {
    setDeleteAccountDialogOpen(true);
  };

  const handleDeleteAccount = async () => {
    setIsDeletingAccount(true);
    setDeleteAccountDialogOpen(false);
    try {
      const result = await deleteAccount();
      if (result.status === "success") {
        toast.success("Account deleted successfully");
        // Logout and redirect to login page
        await handleLogout();
      } else {
        toast.error(result.message || "Failed to delete account");
        setIsDeletingAccount(false);
      }
    } catch (error: any) {
      console.error("Error deleting account:", error);
      toast.error(error?.message || "Failed to delete account");
      setIsDeletingAccount(false);
    }
  };

  if (!activeOrg) {
    if (orgs.length === 0 && !isLoading) {
      return (
        <div className="flex flex-col items-center justify-center gap-2">
          <div className="flex items-center justify-center py-2">
            <AlertTriangle className="text-muted-foreground size-7 mr-2" />
            <p className="text-muted-foreground">You are not a member of any organizations</p>
          </div>
          <Button 
            variant="destructive" 
            onClick={handleDeleteAccountClick}
            disabled={isDeletingAccount}
          >
            {isDeletingAccount ? (
              <>
                <Loader2 className="mr-2 size-4 animate-spin" />
                Deleting...
              </>
            ) : (
              "Delete Account"
            )}
          </Button>
          <AlertDialog open={deleteAccountDialogOpen} onOpenChange={setDeleteAccountDialogOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2 text-red-600">
                  <AlertTriangle className="h-5 w-5" />
                  Delete Account
                </AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete your account? This action cannot be undone and will permanently delete your account and all associated data.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isDeletingAccount}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteAccount}
                  disabled={isDeletingAccount}
                  className="bg-destructive hover:bg-destructive/90"
                >
                  {isDeletingAccount ? (
                    <>
                      <Loader2 className="mr-2 size-4 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    "Delete Account"
                  )}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      );
    }
    return null;
  }

  return (
    <>
      <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SidebarMenuButton 
          size="lg"
          className="mt-[10px] data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
        >
          <div className="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg">
            {isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <GalleryVerticalEnd className="size-4" />
            )}
          </div>
          <div className="grid flex-1 text-left text-sm leading-tight">
            <span className="truncate font-semibold">{activeOrg.name}</span>
            {activeOrg.advised && <span className="truncate text-xs text-muted-foreground">A client you advise</span>}
          </div>
          <CaretSortIcon className="ml-auto" />
        </SidebarMenuButton>
      </DropdownMenuTrigger>
      
      <DropdownMenuContent
        onScroll={handleScroll}
        className="w-(--radix-dropdown-menu-trigger-width) min-w-56 max-h-80 rounded-lg"
        align="start"
        side={"bottom"}
        sideOffset={4}
      >
        <DropdownMenuLabel className="text-muted-foreground text-xs">
          Organizations
        </DropdownMenuLabel>
        <CreateOrgDialogTrigger onTriggerClick={handleCreateOrgClick} />
        <DropdownMenuSeparator />
        {orgs.length === 0 && (
          <div className="my-2 flex h-16 flex-col items-center justify-center gap-2 rounded border border-dashed">
            <div className="text-center text-xs font-normal text-neutral-500">
              No organizations found
            </div>
          </div>
        )}
        {ownOrgs.map((org) => {
          return (
            <DropdownMenuItem
              key={org.id}
              {...(isPending ? { disabled: true } : {})}
              onClick={() => handleSwitchOrg(org.id)}
              className="gap-2 p-2"
              data-testid={`switch-org-${org.id}`}
            >
              <div className="flex size-6 items-center justify-center rounded-sm border">
                <GalleryVerticalEnd className="size-4 shrink-0" />
              </div>
              {org.name}
            </DropdownMenuItem>
          );
        })}
        {advisedOrgs.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-muted-foreground text-xs">
              Clients you advise
            </DropdownMenuLabel>
            {advisedOrgs.map((org) => (
              <DropdownMenuItem
                key={org.id}
                {...(isPending ? { disabled: true } : {})}
                onClick={() => handleSwitchOrg(org.id)}
                className="gap-2 p-2"
                data-testid={`switch-org-${org.id}`}
              >
                <div className="flex size-6 items-center justify-center rounded-sm border border-amber-300 bg-amber-50">
                  <Briefcase className="size-4 shrink-0 text-amber-700" />
                </div>
                {org.name}
              </DropdownMenuItem>
            ))}
          </>
        )}
        {isFetchingNextPage && (
          <div className="flex items-center justify-center py-2">
            <Loader2 className="size-4 animate-spin text-muted-foreground" />
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
    
      {/* Dialog rendered outside of dropdown menu */}
      <CreateOrgDialog 
        open={createOrgDialogOpen}
        onOpenChange={setCreateOrgDialogOpen}
      />
    </>
  );
}
